import {
  ArchivingJobExperimentStatusDto,
  IArchivingJobExperimentResultDTO,
  IPreviewResultRowDTO,
} from 'business/models/Models';

export const normalizeJobDataObj = (dataObj: unknown): Record<string, unknown> | null => {
  if (dataObj == null) {
    return null;
  }

  if (typeof dataObj === 'string') {
    try {
      const parsed = JSON.parse(dataObj);
      return typeof parsed === 'object' && parsed != null
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }

  if (typeof dataObj === 'object') {
    return dataObj as Record<string, unknown>;
  }

  return null;
};

export const getArchivedExperimentIds = (dataObj: unknown) => {
  const normalized = normalizeJobDataObj(dataObj);
  if (!normalized) {
    return [] as number[];
  }

  const ids = normalized.archivedExperimentIds ?? normalized.ArchivedExperimentIds ?? [];

  if (!Array.isArray(ids)) {
    return [] as number[];
  }

  return ids
    .map((id) => (typeof id === 'number' ? id : Number(id)))
    .filter((id) => Number.isFinite(id));
};

export const getArchiveCurrentLocation = (dataObj: unknown): string | null => {
  const normalized = normalizeJobDataObj(dataObj);
  if (!normalized) {
    return null;
  }

  const location = normalized.currentLocation ?? normalized.CurrentLocation;
  return typeof location === 'string' && location.length > 0 ? location : null;
};

// Unlike archiving (one zip shared by the whole job), each restored experiment gets its own tree
// location, so CurrentLocation on an unarchive job's progress is scoped to whichever single
// experiment most recently finished — identified by lastRestoredExperimentId.
export const getLastRestoredExperimentId = (dataObj: unknown): number | null => {
  const normalized = normalizeJobDataObj(dataObj);
  if (!normalized) {
    return null;
  }

  const id = normalized.lastRestoredExperimentId ?? normalized.LastRestoredExperimentId;
  const numericId = typeof id === 'number' ? id : Number(id);
  return Number.isFinite(numericId) ? numericId : null;
};

export const markPreviewRowsArchived = (
  rows: IPreviewResultRowDTO[],
  archivedExperimentIds: number[],
) => {
  if (archivedExperimentIds.length === 0) {
    return;
  }

  const archivedIds = new Set(archivedExperimentIds.map((id) => Number(id)));
  rows.forEach((row) => {
    const rowId = Number(row.id);
    if (archivedIds.has(rowId)) {
      row.isArchived = true;
    }
  });
};

export const markArchivedResults = (
  results: IArchivingJobExperimentResultDTO[],
  archivedExperimentIds: number[],
  currentLocation: string | null = null,
) => {
  if (archivedExperimentIds.length === 0) {
    return;
  }

  const archivedIds = new Set(archivedExperimentIds.map((id) => Number(id)));
  results.forEach((row) => {
    if (!archivedIds.has(row.experimentId)) {
      return;
    }

    row.status = ArchivingJobExperimentStatusDto.Completed;
    row.archivedDate = row.archivedDate ?? new Date();
    if (currentLocation) {
      row.currentLocation = currentLocation;
    }
  });
};

const mergeByExperimentId = <T extends IArchivingJobExperimentResultDTO>(
  existing: T[],
  incoming: T[] | null | undefined,
  onOverlap: (previous: T, incomingRow: T) => T = (previous, incomingRow) => ({
    ...previous,
    ...incomingRow,
  }),
): T[] => {
  if (!incoming || incoming.length === 0) {
    return existing;
  }

  const merged = existing.map((row) => ({ ...row }));
  const indexByExperimentId = new Map(
    merged.map((row, index) => [row.experimentId, index]),
  );

  incoming.forEach((row) => {
    const index = indexByExperimentId.get(row.experimentId);
    if (index === undefined) {
      indexByExperimentId.set(row.experimentId, merged.length);
      merged.push(row);
      return;
    }

    merged[index] = onOverlap(merged[index], row);
  });

  return merged;
};

// Accumulate archive result rows across jobs. Existing order is kept; new experimentIds are
// appended. Overlapping ids take incoming fields. Null/empty incoming is a no-op so a stale or
// empty response cannot wipe results from a previous job.
export const mergeArchivingResults = (
  existing: IArchivingJobExperimentResultDTO[],
  incoming: IArchivingJobExperimentResultDTO[] | null | undefined,
): IArchivingJobExperimentResultDTO[] => mergeByExperimentId(existing, incoming);

// An unarchive result row carries a client-stamped restoredDate (there is no such field on the
// generated DTO — the backend result carries only status/archivedDate).
export type UnarchiveResultRow = IArchivingJobExperimentResultDTO & {
  restoredDate?: Date;
};

// Same accumulation as archive results, but a client-stamped restoredDate is kept when the
// incoming row does not have one.
export const mergeUnarchivingResults = (
  existing: UnarchiveResultRow[],
  incoming: UnarchiveResultRow[] | null | undefined,
): UnarchiveResultRow[] =>
  mergeByExperimentId(existing, incoming, (previous, row) => ({
    ...previous,
    ...row,
    restoredDate: row.restoredDate ?? previous.restoredDate,
  }));

// Flip restored experiment rows to Completed as the unarchive job reports them via the hub.
// The hub payload carries the restored ids in the same ArchivedExperimentIds field, so
// getArchivedExperimentIds is reused. currentLocation/lastRestoredExperimentId identify the single
// experiment that just finished (each restore has its own tree location, unlike archiving's one
// zip shared by the whole job), so that location is applied to that one row only — never the
// whole restoredExperimentIds batch.
export const markUnarchivedResults = (
  results: UnarchiveResultRow[],
  restoredExperimentIds: number[],
  currentLocation: string | null = null,
  lastRestoredExperimentId: number | null = null,
) => {
  if (restoredExperimentIds.length === 0) {
    return;
  }

  const restoredIds = new Set(restoredExperimentIds.map((id) => Number(id)));
  results.forEach((row) => {
    if (!restoredIds.has(row.experimentId)) {
      return;
    }

    row.status = ArchivingJobExperimentStatusDto.Completed;
    row.restoredDate = row.restoredDate ?? new Date();
    if (currentLocation && row.experimentId === lastRestoredExperimentId) {
      row.currentLocation = currentLocation;
    }
  });
};
